import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc, orderBy, addDoc, updateDoc, Timestamp, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../utils/firebase';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MainLayout from '../../components/layout/MainLayout';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import Button from '../../components/ui/Button';
import { COLLECTIONS } from '../../utils/firebase';

interface Game {
  id: string;
  title: string;
  description: string;
  gameUrl: string;
  simulatorCode: string;
  processUrl: string;
  companyName: string;
  creatorName: string;
  schoolName: string;
  schoolId: string;
  userId: string;
  createdAt: Date;
  rating: number;
  playCount: number;
  iconUrl?: string;
  introduction?: string;
  className?: string;
  studentNumber?: number;
  grade?: number;
  class?: number;
  isEvaluationGame?: boolean;
  evaluationCode?: string;
  evaluationInfo?: {
    schoolId: string;
    schoolName: string;
    grade: number;
    startDate: any;
    endDate: any;
    reviewerClasses: {[key: string]: string};
  };
  commentsOnly?: boolean;
  allowGuestComments?: boolean;
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '12px 16px', border: '1px solid #F0F0F0', borderRadius: 8, fontSize: 14, fontFamily: 'Pretendard', boxSizing: 'border-box' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 14, fontWeight: 500, color: '#161616', marginBottom: 8 };

const formatDate = (d: any) => {
  if (!d) return '-';
  const date = d?.toDate ? d.toDate() : d instanceof Date ? d : new Date(d);
  return date.toLocaleDateString('ko-KR');
};

const GameManagement: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const { isMobile } = useLayoutContext();
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState<Game[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [gameToDelete, setGameToDelete] = useState<Game | null>(null);
  
  // 필터 상태
  const [gradeFilter, setGradeFilter] = useState<string>('');
  const [classFilter, setClassFilter] = useState<string>('');
  const [titleFilter, setTitleFilter] = useState<string>('');
  const [grades, setGrades] = useState<number[]>([]);
  const [classes, setClasses] = useState<number[]>([]);
  
  // 페이지네이션 상태
  const [currentPage, setCurrentPage] = useState(1);
  const gamesPerPage = 10;
  
  const [openSubjectCodeDialog, setOpenSubjectCodeDialog] = useState(false);
  const [subjectCode, setSubjectCode] = useState<string | null>(null);
  
  // 게임 등록 다이얼로그 관련 상태
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<any>(null);
  const [loadingSchoolInfo, setLoadingSchoolInfo] = useState(true);
  
  // 게임 등록 폼 데이터
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    gameUrl: '',
    simulatorCode: '',
    processUrl: '',
    companyName: '',
    introduction: '',
    grade: '',
    class: '',
  });
  
  // 아이콘 관련 상태
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  
  // 평가용 게임 관련 상태
  const [isEvaluationGame, setIsEvaluationGame] = useState(false);
  const [evaluationCode, setEvaluationCode] = useState('');
  const [evaluationInfo, setEvaluationInfo] = useState<Game['evaluationInfo'] | null>(null);
  const [evaluationCodeError, setEvaluationCodeError] = useState('');
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [commentsOnly, setCommentsOnly] = useState(false);
  const [allowGuestComments, setAllowGuestComments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // 권한 확인 - useMemo 사용
  const isAuthorized = useMemo(() => {
    console.log('권한 계산 중 - 현재 사용자:', currentUser);
    console.log('권한 계산 중 - 사용자 역할:', userRole);
    
    if (!currentUser || !userRole) {
      console.log('권한 계산 결과: false (사용자 또는 역할 없음)');
      return false;
    }
    
    if (userRole === 'teacher' || userRole === 'superAdmin' || userRole === 'schoolAdmin') {
      console.log('권한 계산 결과: true (허용된 역할)');
      return true;
    }
    
    console.log('권한 계산 결과: false (허용되지 않은 역할)');
    return false;
  }, [currentUser, userRole]);
  
  // 학교 정보 가져오기
  useEffect(() => {
    if (!currentUser) {
      setLoadingSchoolInfo(false);
      return;
    }
    
    const fetchSchoolInfo = async () => {
      try {
        setLoadingSchoolInfo(true);
        
        let schoolId = currentUser.schoolId;
        
        if (!schoolId && currentUser.uid) {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            schoolId = userData.schoolId;
          }
        }
        
        if (schoolId) {
          const schoolDoc = await getDoc(doc(db, 'schools', schoolId));
          if (schoolDoc.exists()) {
            setSchoolInfo(schoolDoc.data());
          }
        }
      } catch (error) {
        console.error('Error fetching school info:', error);
      } finally {
        setLoadingSchoolInfo(false);
      }
    };
    
    fetchSchoolInfo();
  }, [currentUser]);

  // 게임 데이터 가져오기
  useEffect(() => {
    if (!isAuthorized) return;
    
    const fetchGames = async () => {
      try {
        if (!currentUser?.schoolId) {
          setError('학교 정보가 없습니다.');
          setLoading(false);
          return;
        }
        
        console.log('게임 데이터 가져오기 시작 - 학교 ID:', currentUser.schoolId);
        console.log('현재 사용자 정보:', currentUser);
        
        // 먼저 모든 게임을 가져와서 확인
        const allGamesQuery = query(
          collection(db, 'games'),
          orderBy('createdAt', 'desc')
        );
        
        const allGamesSnapshot = await getDocs(allGamesQuery);
        console.log('전체 게임 문서 수:', allGamesSnapshot.size);
        
        // 모든 게임 데이터 로깅
        allGamesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          console.log('전체 게임 데이터:', { id: doc.id, schoolId: data.schoolId, title: data.title });
        });
        
        // 학교의 모든 게임 가져오기
        const gamesQuery = query(
          collection(db, 'games'),
          where('schoolId', '==', currentUser.schoolId),
          orderBy('createdAt', 'desc')
        );
        
        const gamesSnapshot = await getDocs(gamesQuery);
        console.log('필터링된 게임 문서 수:', gamesSnapshot.size);
        
        const gamesList = gamesSnapshot.docs.map(doc => {
          const data = doc.data();
          console.log('필터링된 게임 데이터:', { id: doc.id, ...data });
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
          };
        }) as Game[];
        
        console.log('처리된 게임 목록:', gamesList);
        
        setGames(gamesList);
        setFilteredGames(gamesList);
        
        // 클래스와 번호 목록 추출
        const gradeSet = new Set<number>();
        const classSet = new Set<number>();
        
        gamesList.forEach(game => {
          // 새로운 필드 구조 (grade, class) 사용
          if (game.grade) {
            gradeSet.add(game.grade);
          }
          if (game.class) {
            classSet.add(game.class);
          }
          
          // 기존 className 필드도 지원 (하위 호환성)
          if (game.className) {
            const match = game.className.match(/(\d+)학년\s*(\d+)반/);
            if (match) {
              const grade = parseInt(match[1]);
              const classNum = parseInt(match[2]);
              gradeSet.add(grade);
              classSet.add(classNum);
            }
          }
        });
        
        setGrades(Array.from(gradeSet).sort((a, b) => a - b));
        setClasses(Array.from(classSet).sort((a, b) => a - b));
        
      } catch (error) {
        console.error('Error fetching games:', error);
        setError('게임 데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchGames();
  }, [currentUser, isAuthorized]);
  
  // 필터 적용
  useEffect(() => {
    if (!isAuthorized) return;
    
    let filtered = [...games];
    
    // 제목 검색 필터
    if (titleFilter.trim()) {
      filtered = filtered.filter(game => 
        game.title.toLowerCase().includes(titleFilter.toLowerCase().trim())
      );
    }
    
    if (gradeFilter) {
      filtered = filtered.filter(game => {
        // 새로운 필드 구조 (grade) 사용
        if (game.grade) {
          return game.grade === parseInt(gradeFilter);
        }
        
        // 기존 className 필드도 지원 (하위 호환성)
        if (game.className) {
          const match = game.className.match(/(\d+)학년\s*(\d+)반/);
          return match && parseInt(match[1]) === parseInt(gradeFilter);
        }
        
        return false;
      });
    }
    
    if (classFilter) {
      filtered = filtered.filter(game => {
        // 새로운 필드 구조 (class) 사용
        if (game.class) {
          return game.class === parseInt(classFilter);
        }
        
        // 기존 className 필드도 지원 (하위 호환성)
        if (game.className) {
          const match = game.className.match(/(\d+)학년\s*(\d+)반/);
          return match && parseInt(match[2]) === parseInt(classFilter);
        }
        
        return false;
      });
    }
    
    setFilteredGames(filtered);
    setCurrentPage(1); // 필터 변경 시 첫 페이지로 이동
  }, [games, titleFilter, gradeFilter, classFilter, isAuthorized]);
  
  const handleGameClick = (gameId: string) => {
    navigate(`/makecodearcade/game/${gameId}`);
  };
  
  const handleDeleteClick = (game: Game) => {
    setGameToDelete(game);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!gameToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'games', gameToDelete.id));
      
      // 상태 업데이트
      setGames(games.filter(game => game.id !== gameToDelete.id));
      setFilteredGames(filteredGames.filter(game => game.id !== gameToDelete.id));
      
      setDeleteDialogOpen(false);
      setGameToDelete(null);
      toast.success('게임이 삭제되었습니다.');
    } catch (error) {
      console.error('Error deleting game:', error);
      toast.error('게임을 삭제하는 중 오류가 발생했습니다.');
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setGameToDelete(null);
  };
  
  const handleFilterReset = () => {
    setGradeFilter('');
    setClassFilter('');
    setTitleFilter('');
    setCurrentPage(1); // 필터 초기화 시 첫 페이지로 이동
  };
  
  // 교과 코드 생성
  const generateSubjectCode = async () => {
    if (!currentUser?.schoolId) {
      toast.error('학교 정보가 없습니다.');
      return;
    }
    
    try {
      setError(null);
      setLoading(true);
      
      // 교과 코드가 이미 있는지 확인
      const subjectCodeRef = collection(db, 'subjectCodes');
      const q = query(
        subjectCodeRef,
        where('schoolId', '==', currentUser.schoolId)
      );
      const querySnapshot = await getDocs(q);

      // 기존 교과 코드가 있으면 재사용, 없으면 새로 생성
      if (!querySnapshot.empty) {
        const existingCode = querySnapshot.docs[0].data().code;
        setSubjectCode(existingCode);
        setOpenSubjectCodeDialog(true);
        return;
      } else {
        // 6자리 랜덤 코드 생성
        const code = Math.random().toString(36).substring(2, 8).toUpperCase();

        // 교과 코드 문서 생성 및 추가
        await addDoc(collection(db, 'subjectCodes'), {
          code,
          schoolId: currentUser.schoolId,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        setSubjectCode(code);
        setOpenSubjectCodeDialog(true);
      }
    } catch (err) {
      console.error('교과 코드 생성 중 오류:', err);
      toast.error('교과 코드 생성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSubjectCodeDialog = () => {
    setOpenSubjectCodeDialog(false);
    setSubjectCode(null);
  };

  // 게임 등록 관련 핸들러들
  const handleOpenRegisterDialog = () => {
    setRegisterDialogOpen(true);
    setEditingGameId(null);
    resetForm();
  };

  const handleCloseRegisterDialog = () => {
    setRegisterDialogOpen(false);
    setEditingGameId(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      gameUrl: '',
      simulatorCode: '',
      processUrl: '',
      companyName: '',
      introduction: '',
      grade: '',
      class: '',
    });
    setIconFile(null);
    setIconPreview(null);
    setIsEvaluationGame(false);
    setEvaluationCode('');
    setEvaluationInfo(null);
    setEvaluationCodeError('');
    setCommentsOnly(false);
    setAllowGuestComments(false);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleIconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('파일 크기는 5MB를 초과할 수 없습니다.');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('이미지 파일만 업로드 가능합니다.');
        return;
      }
      
      setIconFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setIconPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEvaluationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEvaluationCode(e.target.value);
    setEvaluationCodeError('');
  };

  const checkEvaluationCode = async () => {
    if (!evaluationCode.trim()) {
      setEvaluationCodeError('평가용 코드를 입력해주세요.');
      return;
    }

    setIsCheckingCode(true);
    setEvaluationCodeError('');

    try {
      const evaluationCodesRef = collection(db, 'evaluationCodes');
      const q = query(evaluationCodesRef, where('code', '==', evaluationCode));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setEvaluationCodeError('유효하지 않은 평가용 코드입니다.');
        setEvaluationInfo(null);
      } else {
        const codeDoc = querySnapshot.docs[0];
        const codeData = codeDoc.data();
        
        const parsedStartDate = codeData.startDate?.toDate ? codeData.startDate.toDate() : 
                               codeData.startDate instanceof Date ? codeData.startDate : 
                               new Date(codeData.startDate);
        const parsedEndDate = codeData.endDate?.toDate ? codeData.endDate.toDate() : 
                             codeData.endDate instanceof Date ? codeData.endDate : 
                             new Date(codeData.endDate);
        
        if (isNaN(parsedStartDate.getTime()) || isNaN(parsedEndDate.getTime())) {
          setEvaluationCodeError('평가 기간 정보가 올바르지 않습니다.');
          setEvaluationInfo(null);
          return;
        }
        
        setEvaluationInfo({
          schoolId: codeData.schoolId,
          schoolName: codeData.schoolName,
          grade: codeData.grade,
          startDate: parsedStartDate,
          endDate: parsedEndDate,
          reviewerClasses: codeData.reviewerClasses || {}
        });
        
        setEvaluationCodeError('');
      }
    } catch (error) {
      console.error('Error checking evaluation code:', error);
      setEvaluationCodeError('평가용 코드 확인 중 오류가 발생했습니다.');
      setEvaluationInfo(null);
    } finally {
      setIsCheckingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      toast.error('로그인이 필요합니다.');
      return;
    }
    
    if (!formData.title || !formData.gameUrl || !formData.simulatorCode) {
      toast.error('게임 제목, 게임 URL, 시뮬레이터 코드는 필수입니다.');
      return;
    }
    
    if (isEvaluationGame && !evaluationInfo) {
      toast.error('평가용 게임을 등록하려면 유효한 평가용 코드가 필요합니다.');
      return;
    }
    
    setLoading(true);
    
    try {
      let iconUrl = '';
      
      if (iconFile && storage) {
        setUploadingIcon(true);
        const storageRef = ref(storage, `game-icons/${currentUser.uid}/${Date.now()}_${iconFile.name}`);
        await uploadBytes(storageRef, iconFile);
        iconUrl = await getDownloadURL(storageRef);
        setUploadingIcon(false);
      }
      
      const gameData: any = {
        title: formData.title,
        description: formData.description,
        gameUrl: formData.gameUrl,
        simulatorCode: formData.simulatorCode,
        processUrl: formData.processUrl,
        companyName: formData.companyName,
        creatorName: currentUser.name || '익명',
        schoolName: schoolInfo?.name || '미지정',
        userId: currentUser.uid,
        introduction: formData.introduction,
        grade: formData.grade ? parseInt(formData.grade) : null,
        class: formData.class ? parseInt(formData.class) : null,
      };
      
      if (currentUser.schoolId) {
        gameData.schoolId = currentUser.schoolId;
      }
      
      if (iconUrl) {
        gameData.iconUrl = iconUrl;
      }
      
      if (isEvaluationGame && evaluationInfo) {
        gameData.isEvaluationGame = true;
        gameData.evaluationCode = evaluationCode;
        gameData.evaluationInfo = {
          schoolId: evaluationInfo.schoolId,
          schoolName: evaluationInfo.schoolName,
          grade: evaluationInfo.grade,
          startDate: evaluationInfo.startDate instanceof Date ? Timestamp.fromDate(evaluationInfo.startDate) : evaluationInfo.startDate,
          endDate: evaluationInfo.endDate instanceof Date ? Timestamp.fromDate(evaluationInfo.endDate) : evaluationInfo.endDate,
          reviewerClasses: evaluationInfo.reviewerClasses
        };
      } else {
        gameData.isEvaluationGame = false;
        gameData.evaluationCode = null;
        gameData.evaluationInfo = null;
      }
      gameData.commentsOnly = commentsOnly;
      gameData.allowGuestComments = allowGuestComments;
      
      if (editingGameId) {
        await updateDoc(doc(db, 'games', editingGameId), gameData);
        toast.success('게임이 성공적으로 수정되었습니다!');
        setEditingGameId(null);
      } else {
        gameData.createdAt = new Date();
        gameData.rating = 0;
        gameData.playCount = 0;
        await addDoc(collection(db, 'games'), gameData);
        toast.success('게임이 성공적으로 등록되었습니다!');
      }
      
      handleCloseRegisterDialog();
      
      // 게임 목록 새로고침
      const gamesQuery = query(
        collection(db, 'games'),
        where('schoolId', '==', currentUser.schoolId),
        orderBy('createdAt', 'desc')
      );
      const gamesSnapshot = await getDocs(gamesQuery);
      const gamesList = gamesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as Game[];
      setGames(gamesList);
      setFilteredGames(gamesList);
      
    } catch (error) {
      console.error('Error registering game:', error);
      toast.error('게임 등록 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditGame = (game: Game) => {
    setEditingGameId(game.id);
    setFormData({
      title: game.title,
      description: game.description,
      gameUrl: game.gameUrl,
      simulatorCode: game.simulatorCode,
      processUrl: game.processUrl,
      companyName: game.companyName,
      introduction: game.introduction || '',
      grade: game.grade?.toString() || '',
      class: game.class?.toString() || '',
    });
    
    if (game.iconUrl) {
      setIconPreview(game.iconUrl);
    }
    
    if (game.isEvaluationGame) {
      setIsEvaluationGame(true);
      setEvaluationCode(game.evaluationCode || '');
      
      if (game.evaluationInfo) {
        const startDate = game.evaluationInfo.startDate?.toDate ? game.evaluationInfo.startDate.toDate() : 
                         game.evaluationInfo.startDate instanceof Date ? game.evaluationInfo.startDate : 
                         new Date(game.evaluationInfo.startDate);
        const endDate = game.evaluationInfo.endDate?.toDate ? game.evaluationInfo.endDate.toDate() : 
                       game.evaluationInfo.endDate instanceof Date ? game.evaluationInfo.endDate : 
                       new Date(game.evaluationInfo.endDate);
        
        setEvaluationInfo({
          ...game.evaluationInfo,
          startDate: startDate,
          endDate: endDate
        });
      } else {
        setEvaluationInfo(null);
      }
    } else {
      setIsEvaluationGame(false);
      setEvaluationCode('');
      setEvaluationInfo(null);
    }
    setCommentsOnly(Boolean(game.commentsOnly));
    setAllowGuestComments(Boolean(game.allowGuestComments));
    
    setRegisterDialogOpen(true);
  };
  
  // 권한이 없는 경우 리디렉션
  if (!isAuthorized) {
    return <Navigate to="/makecodearcade/ArcadeMain" replace />;
  }
  
  if (loading) {
    return (
      <MainLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ fontSize: 14 }}>로딩 중...</div>
        </div>
      </MainLayout>
    );
  }
  
  if (error) {
    return (
      <MainLayout>
        <div style={getMakecodeArcadePageRootStyle(isMobile)}>
          <div style={{ padding: 16, background: '#FFE5E5', color: '#C33333', borderRadius: 8, marginBottom: 16 }}>{error}</div>
          <Button variant="primary" onClick={() => navigate('/makecodearcade')}>홈으로 돌아가기</Button>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <div style={getMakecodeArcadePageRootStyle(isMobile)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
          <div style={{ fontSize: 24, fontWeight: 600 }}>학급 게임 관리</div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button variant="primary" onClick={handleOpenRegisterDialog} disabled={loading}>+ 게임 등록</Button>
            <Button variant="outline" onClick={generateSubjectCode} disabled={loading}>교과 코드 생성</Button>
          </div>
        </div>
        
        {/* 필터 섹션 */}
        <div style={{ padding: 24, marginBottom: 24, background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <span>필터</span>
            <div style={{ flex: 1 }} />
            <Button variant="outline" onClick={handleFilterReset} disabled={!gradeFilter && !classFilter}>필터 초기화</Button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>게임 제목 검색</label>
              <input value={titleFilter} onChange={(e) => setTitleFilter(e.target.value)} placeholder="게임 제목을 입력하세요" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={labelStyle}>학년</label>
                <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} style={inputStyle}>
                  <option value="">전체</option>
                  {grades.map((g) => <option key={g} value={g}>{g}학년</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={labelStyle}>반</label>
                <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} style={inputStyle}>
                  <option value="">전체</option>
                  {classes.map((c) => <option key={c} value={c}>{c}반</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        {/* 게임 목록 */}
        <div style={{ padding: 24, background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #F0F0F0' }}>
            우리 학교 학생들의 게임 ({filteredGames.length}개)
          </div>
          
          {filteredGames.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#666' }}>표시할 게임이 없습니다.</div>
          ) : (
            <>
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, color: '#666' }}>
                {((currentPage - 1) * gamesPerPage) + 1} - {Math.min(currentPage * gamesPerPage, filteredGames.length)} / {filteredGames.length}개
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {filteredGames.slice((currentPage - 1) * gamesPerPage, currentPage * gamesPerPage).map((game) => (
                  <div 
                    key={game.id} 
                    style={{ display: 'flex', padding: 16, background: '#FAFAFA', borderRadius: 12, alignItems: 'center', gap: 16, flexWrap: 'wrap', cursor: 'pointer' }}
                    onClick={() => handleGameClick(game.id)}
                  >
                    <img src={game.iconUrl || '/images/icons/Logo.svg'} alt={game.title} style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover', background: '#F0F0F0' }} />
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{game.title}</div>
                      <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
                        작성자: {game.creatorName} • {game.grade && game.class ? `${game.grade}학년 ${game.class}반` : game.grade ? `${game.grade}학년` : game.class ? `${game.class}반` : game.className || '학년/반 미지정'}{game.studentNumber ? ` • ${game.studentNumber}번` : ''}
                      </div>
                      {game.introduction && <div style={{ fontSize: 14, color: '#666', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>"{game.introduction}"</div>}
                      <div style={{ marginTop: 8, fontSize: 12, color: '#989898' }}>등록일: {formatDate(game.createdAt)}{game.companyName ? ` • 제작: ${game.companyName}` : ''}{game.isEvaluationGame ? ' • ⭐ 평가용 게임' : ''}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8, fontSize: 14 }}>⭐ {game.rating.toFixed(1)} | ▶ {game.playCount.toLocaleString()}회</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                      <Button variant="primary" size="small" onClick={() => handleGameClick(game.id)}>상세</Button>
                      <Button variant="outline" size="small" onClick={() => handleEditGame(game)}>수정</Button>
                      <Button variant="cancel" size="small" onClick={() => handleDeleteClick(game)}>삭제</Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {filteredGames.length > gamesPerPage && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
                  <Button variant="outline" size="small" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>처음</Button>
                  <Button variant="outline" size="small" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>이전</Button>
                  <span style={{ display: 'flex', alignItems: 'center', padding: '0 16px', fontSize: 14 }}>{currentPage} / {Math.ceil(filteredGames.length / gamesPerPage)}</span>
                  <Button variant="outline" size="small" disabled={currentPage >= Math.ceil(filteredGames.length / gamesPerPage)} onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredGames.length / gamesPerPage), p + 1))}>다음</Button>
                  <Button variant="outline" size="small" disabled={currentPage >= Math.ceil(filteredGames.length / gamesPerPage)} onClick={() => setCurrentPage(Math.ceil(filteredGames.length / gamesPerPage))}>마지막</Button>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* 삭제 확인 다이얼로그 */}
        {deleteDialogOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }} onClick={handleDeleteCancel}>
            <div style={{ background: 'white', borderRadius: 16, padding: 24, maxWidth: 400, width: '90%' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>게임 삭제 확인</div>
              <div style={{ marginBottom: 24 }}>정말로 "{gameToDelete?.title}" 게임을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.</div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <Button variant="outline" onClick={handleDeleteCancel}>취소</Button>
                <Button variant="cancel" onClick={handleDeleteConfirm}>삭제</Button>
              </div>
            </div>
          </div>
        )}
        
        {/* 교과 코드 다이얼로그 */}
        {openSubjectCodeDialog && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }} onClick={handleCloseSubjectCodeDialog}>
            <div style={{ background: 'white', borderRadius: 16, padding: 24, maxWidth: 400, width: '90%' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 16 }}>교과 코드</div>
              <div style={{ marginBottom: 16 }}>아래 코드를 학생들에게 공유해주세요. 이 코드를 사용하여 학생들이 등록할 수 있습니다.</div>
              <div style={{ padding: 16, background: '#F5F5F5', textAlign: 'center', fontFamily: 'monospace', fontSize: '1.25rem', letterSpacing: '0.2em', borderRadius: 8, marginBottom: 24 }}>{subjectCode}</div>
              <Button variant="primary" onClick={handleCloseSubjectCodeDialog}>닫기</Button>
            </div>
          </div>
        )}

        {/* 게임 등록/수정 다이얼로그 */}
        {registerDialogOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, overflow: 'auto' }} onClick={handleCloseRegisterDialog}>
            <div style={{ background: 'white', borderRadius: 16, padding: 24, maxWidth: 640, width: '90%', maxHeight: '90vh', overflow: 'auto', margin: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 24 }}>{editingGameId ? '게임 수정' : '게임 등록'}</div>
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                    <img src={iconPreview || '/images/icons/Logo.svg'} alt="아이콘" style={{ width: 100, height: 100, borderRadius: 8, objectFit: 'cover', background: '#F5F5F5' }} />
                    <div>
                      <input ref={fileInputRef} accept="image/*" type="file" onChange={handleIconChange} style={{ display: 'none' }} />
                      <Button variant="outline" type="button" disabled={uploadingIcon} onClick={() => fileInputRef.current?.click()}>{uploadingIcon ? '업로드 중...' : '게임 아이콘 업로드'}</Button>
                    </div>
                  </div>
                  <div><label style={labelStyle}>게임 제목 *</label><input name="title" value={formData.title} onChange={handleFormChange} required style={inputStyle} /></div>
                  <div><label style={labelStyle}>게임 설명</label><textarea name="description" value={formData.description} onChange={handleFormChange} rows={4} style={{ ...inputStyle, minHeight: 80 }} /></div>
                  <div><label style={labelStyle}>게임 소개 문구</label><textarea name="introduction" value={formData.introduction} onChange={handleFormChange} rows={3} style={{ ...inputStyle, minHeight: 60 }} /><div style={{ fontSize: 12, color: '#989898', marginTop: 4 }}>게임의 특징과 재미있는 점을 간단히 소개해주세요.</div></div>
                  <div><label style={labelStyle}>게임 URL *</label><input name="gameUrl" value={formData.gameUrl} onChange={handleFormChange} required style={inputStyle} /><div style={{ fontSize: 12, color: '#989898', marginTop: 4 }}>예: https://makecode.com/_MH2WAy9ieYgU</div></div>
                  <div><label style={labelStyle}>시뮬레이터 코드 *</label><textarea name="simulatorCode" value={formData.simulatorCode} onChange={handleFormChange} required rows={4} style={{ ...inputStyle, minHeight: 80 }} /></div>
                  <div><label style={labelStyle}>제작 과정 페이지 URL</label><input name="processUrl" value={formData.processUrl} onChange={handleFormChange} style={inputStyle} /></div>
                  <div><label style={labelStyle}>제작 회사명</label><input name="companyName" value={formData.companyName} onChange={handleFormChange} style={inputStyle} /></div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 120 }}><label style={labelStyle}>학년 (선택)</label><select name="grade" value={formData.grade} onChange={handleSelectChange} disabled={loadingSchoolInfo} style={inputStyle}><option value="">선택하지 않음</option>{loadingSchoolInfo ? <option disabled>로딩 중...</option> : (schoolInfo?.gradeClasses ? Object.keys(schoolInfo.gradeClasses).map((g) => <option key={g} value={g}>{g}학년</option>) : Array.from({ length: 6 }, (_, i) => (i + 1).toString()).map((g) => <option key={g} value={g}>{g}학년</option>))}</select></div>
                    <div style={{ flex: 1, minWidth: 120 }}><label style={labelStyle}>반 (선택)</label><select name="class" value={formData.class} onChange={handleSelectChange} disabled={loadingSchoolInfo} style={inputStyle}><option value="">선택하지 않음</option>{loadingSchoolInfo ? <option disabled>로딩 중...</option> : schoolInfo?.gradeClasses && formData.grade ? Array.from({ length: schoolInfo.gradeClasses[parseInt(formData.grade)] || 10 }, (_, i) => (i + 1).toString()).map((c) => <option key={c} value={c}>{c}반</option>) : schoolInfo?.gradeClasses ? Array.from({ length: Math.max(...Object.values(schoolInfo.gradeClasses || {}).map((v: any) => Number(v) || 0)) || 10 }, (_, i) => (i + 1).toString()).map((c) => <option key={c} value={c}>{c}반</option>) : Array.from({ length: 10 }, (_, i) => (i + 1).toString()).map((c) => <option key={c} value={c}>{c}반</option>)}</select></div>
                  </div>
                  <div style={{ borderTop: '1px solid #F0F0F0', margin: '8px 0', paddingTop: 16 }} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={commentsOnly} onChange={(e) => setCommentsOnly(e.target.checked)} /><span>별점 없이 댓글만 받기</span></label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={allowGuestComments} onChange={(e) => setAllowGuestComments(e.target.checked)} /><span>로그인 없이 별점·리뷰 등록 허용</span></label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}><input type="checkbox" checked={isEvaluationGame} onChange={(e) => setIsEvaluationGame(e.target.checked)} /><span>평가용 게임으로 등록</span></label>
                  {isEvaluationGame && (
                    <div style={{ padding: 16, background: '#F8F8F8', borderRadius: 8 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>평가용 게임 등록</div>
                      <div style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>평가용 게임으로 등록하면 지정된 학급의 학생들만 리뷰와 별점을 남길 수 있습니다.</div>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}><input value={evaluationCode} onChange={handleEvaluationCodeChange} placeholder="평가용 코드" style={{ ...inputStyle, flex: 1, minWidth: 200 }} /><Button variant="primary" type="button" onClick={checkEvaluationCode} disabled={isCheckingCode}>{isCheckingCode ? '확인 중...' : '코드 확인'}</Button></div>
                      {evaluationCodeError && <div style={{ color: '#C33333', fontSize: 12, marginBottom: 8 }}>{evaluationCodeError}</div>}
                      {evaluationInfo && <div style={{ marginTop: 16 }}><div style={{ fontWeight: 600, marginBottom: 8 }}>평가 정보</div><div>학교: {evaluationInfo.schoolName} | 학년: {evaluationInfo.grade}학년</div><div>평가 기간: {formatDate(evaluationInfo.startDate)} ~ {formatDate(evaluationInfo.endDate)}</div><div>평가 대상: {Object.entries(evaluationInfo.reviewerClasses).map(([r, t]) => `${r}반 → ${t}반`).join(', ')}</div></div>}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}><Button type="button" variant="outline" onClick={handleCloseRegisterDialog}>취소</Button><Button type="submit" variant="submit" disabled={loading}>{loading ? '처리 중...' : (editingGameId ? '게임 수정' : '게임 등록')}</Button></div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default GameManagement; 